import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { BrandingService } from '@comparit/infrastructure';
import {
  catchError,
  defaultIfEmpty,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import type { VergleichGruppierungInput } from '../../interfaces/vergleich-input.model';
import { LoadingStatus, VorgangStatus } from '@comparit/common-api';
import type { VorgangInput } from '../../interfaces/vorgang-input.model';
import { antragNotice, contactTerm, settledVorgang } from '../../misc/antrag-notice';
import type { AntragNotice } from '../../misc/antrag-notice';
import { TarifdetailsBaseService } from '../../services/tarifdetails-base.service';

export const VORGANG_UNAVAILABLE_MESSAGE =
  'Der angeforderte Vorgang existiert nicht oder ist nicht mehr verfügbar.';

/** The state behind `ui-tarifdetails-page`, scoped to the component so a reload starts clean. */
@Injectable()
export class TarifdetailsPageService {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly branding = inject(BrandingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tarifdetails = inject(TarifdetailsBaseService);

  private readonly vorgangId = this.route.snapshot.paramMap.get('vorgangId') ?? '';

  /** The Vorgang as first loaded; kept as a signal so the notices can be `computed`. */
  private readonly loadedVorgang = signal<VorgangInput | null>(null);

  /** The Vorgang as re-read while its Antrag was being sent, or after the dialog closed without a result. */
  private readonly reloadedVorgang = signal<VorgangInput | null>(null);

  /** This page saw the Antrag go from being sent back to open, so it did not go through. */
  readonly antragFailed = signal(false);

  /** The page stopped asking for the answer after about two minutes without getting one. */
  private readonly stoppedChecking = signal(false);

  readonly antragFailedMessage = computed(
    () =>
      `Dein Antrag wurde nicht übermittelt. Bitte prüfe deine Antragsdaten und versuche es erneut. Wenn es wieder nicht klappt, wende dich an ${contactTerm(this.branding)}.`
  );

  /** The Vorgang as created by the Laden page — enough to render the page right away. */
  readonly vorgang$ = this.tarifdetails.loadVorgang$(this.vorgangId).pipe(
    catchError(() => throwError(() => new Error(VORGANG_UNAVAILABLE_MESSAGE))),
    tap(vorgang => {
      this.loadedVorgang.set(vorgang);
      this.followWhileSending(vorgang);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Binding price + documents, fetched behind the visible page. The backend holds `get-order`
   * until the insurer answered, returns the stored result for a completed Vorgang and reclaims
   * stale runs, so one call per page load is enough — no polling; a reload is the retry.
   */
  readonly getOrder$ = this.vorgang$.pipe(
    switchMap(() => this.tarifdetails.executeGetOrder$(this.vorgangId)),
    // Anything but Success carries no result: the backend reported Error, or a second tab
    // still holds the run. Both end up as the error state.
    switchMap(vorgang =>
      vorgang.getOrderStatus === LoadingStatus.Success
        ? of(vorgang)
        : throwError(() => new Error(`get-order ended with status ${vorgang.getOrderStatus}`))
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Price from the calculation result, shown until the insurer confirms the binding price. */
  readonly provisionalBruttoBetrag$ = this.vorgang$.pipe(
    take(1),
    switchMap(vorgang => this.tarifdetails.findTarif$(vorgang.ergebnisIdentifier)),
    map(tarif => tarif?.bruttoBetrag ?? null)
  );

  /**
   * The Tarifdaten table. Read through `apiAsync` like the other two calls on this page, so a
   * failing comparison shows a message instead of throwing out of the template and leaving the
   * section on its spinner for good.
   */
  readonly vergleichGroups$ = this.vorgang$.pipe(
    take(1),
    switchMap(vorgang => {
      const ergebnis = vorgang.ergebnisIdentifier;
      return this.tarifdetails.fetchVergleich$(ergebnis).pipe(
        map((response): VergleichGruppierungInput[] => {
          const firstBerechnungId = Object.keys(response)[0];
          if (!firstBerechnungId) return [];
          const data = response[firstBerechnungId][ergebnis.kombinationId];
          return data?.attributGruppierungen ?? [];
        })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  goBack(): void {
    this.tarifdetails.goBack();
  }

  /** A sent or still sending Antrag ends the journey as the success page does: empty inputs, first step. */
  startNewJourney(): void {
    this.tarifdetails.startNewJourney();
  }

  /** Back from an Antrag that did not go through is its offer, where it can be sent again. */
  showOffer(): void {
    this.antragFailed.set(false);
  }

  /** A sent or still sending Antrag replaces the offer, so the tariff cannot be applied for twice. */
  readonly notice = computed<AntragNotice | null>(() => {
    const vorgang = this.reloadedVorgang() ?? this.loadedVorgang();
    return vorgang ? antragNotice(vorgang.status) : null;
  });

  /** Back stays off while the page still waits for the answer, so the customer sees how it ended. */
  readonly waitsForAnswer = computed(
    () => this.notice()?.state === 'busy' && !this.stoppedChecking()
  );

  /**
   * The "Zum Antrag" click. Unset `antragUrl` makes it a no-op rather than a navigation to
   * `[undefined]`, exactly as the Vergleich page's Antrag button behaves.
   */
  openAntrag(vorgang: VorgangInput): void {
    const { antragUrl } = this.tarifdetails;
    if (!antragUrl) return;

    void this.router.navigate([antragUrl], { queryParams: { vorgangId: vorgang.vorgangId } });
  }

  /**
   * Re-reads a Vorgang whose Antrag is being sent until it is through, so the page switches by
   * itself. After about two minutes it stops asking and keeps the notice; a reload asks again.
   */
  private followWhileSending(vorgang: VorgangInput): void {
    if (vorgang.status !== VorgangStatus.ContractPending) return;

    // `settledVorgang` already swallows a failed or hanging read and asks again on the next tick.
    settledVorgang(() => this.tarifdetails.loadVorgang$(this.vorgangId))
      .pipe(defaultIfEmpty(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(settled => {
        if (!settled) {
          this.stoppedChecking.set(true);
          return;
        }
        this.antragFailed.set(settled.status === VorgangStatus.Filled);
        this.reloadedVorgang.set(settled);
      });
  }
}
